import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { Strong, Text } from '@/components/base/text'
import { useEffect, useState } from 'react'
import { EventQuery } from './__generated__/show_query'
import { EventQuery as Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { Badge as ClientTypeBadge } from '@/components/client_type/badge'
import { StateBadge as EventStateBadge } from './badge'
import { BuildingStorefrontIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/solid'
import { format } from 'date-fns'
import { Accordion } from '@/components/accordion'
import { Table as ContactsTable } from '@/components/event_contact/table'
import { Table as ShiftsTable } from '@/components/shift/table'
import { Table as EventItemsTable } from '@/components/event_item/table'
import { Table as EventStagesTable } from '@/components/event_stage/table'
import { Table as DocumentsTable } from '@/components/document/table'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as TableQuery } from './table_query'
import { EditDialog } from './edit_dialog'
import { Mutation as UpdateMutation } from './update_mutation'
import { Query as EditQuery } from './edit_query'
import { useForm } from '@/helpers/useForm'
import { UpdateEventMutation } from './__generated__/update_mutation'
import { EditEventQuery } from './__generated__/edit_query'
import { AttachmentResourceType, DocumentSubjectType, EventAttributes, EventState } from '@/types'
import { BounceLoader } from 'react-spinners'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'
import { CloneDialog } from './clone_dialog'

type ShowEvent = EventQuery['events']['nodes'][0]
type EditEvent = EditEventQuery['events']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openClone, setOpenClone] = useState(false)

  const [openDelete, setOpenDelete] = useState(false)
  const [event, setEvent] = useState<ShowEvent | null>(null)

  const { data, loading, error } = useQuery<EventQuery>(Query, { variables: { filters: { idEq: id } } })

  const {
    attributes,
    setAttributes,
    save,
    mutationLoading
  } = useForm<UpdateEventMutation, EventAttributes, EditEventQuery, EditEvent>({
    id: id || '',
    initializer: event => ({
      description: event.description,
      details: event.details,
      dateOut: event.dateOut,
      dateStart: event.dateStart,
      dateEnd: event.dateEnd,
      dateReturn: event.dateReturn,
      state: event.state,
      clientId: event.client.id,
      venueId: event.venue?.id,
      warehouseId: event.warehouse?.id
    }),
    query: EditQuery,
    queryKey: 'events',
    mutation: UpdateMutation,
    resultKey: 'updateEvent'
  })

  useEffect(() => {
    if (!data || !id) return

    setEvent(data.events.nodes[0])
  }, [id, data])

  const saveInAMo = () => { setTimeout(save, 100) }

  const onConfirm = () => {
    setAttributes(() => ({ ...attributes, state: EventState.Confirmed }))
    saveInAMo()
  }
  const onPending = () => {
    setAttributes(() => ({ ...attributes, state: EventState.Pending }))
    saveInAMo()
  }
  const onCancel = () => {
    setAttributes(() => ({ ...attributes, state: EventState.Cancelled }))
    saveInAMo()
  }
  const onEdit = () => {
    setOpenEdit(true)
  }
  const onDelete = () => {
    setOpenDelete(true)
  }

  const onClone = () => {
    setOpenClone(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          {
            loading || !event
              ? <Loading />
              : <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { event.description }
                    <EventStateBadge event={event} />
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <UserIcon className="h-6 w-6" />
                    <Subheading>{ event.client.organisationName }</Subheading>
                    <ClientTypeBadge clientType={event.client.clientType} />
                  </span>
                  { event.venue && <span className="flex flex-row gap-2 items-center">
                    <MapPinIcon className="h-6 w-6" />
                    <Subheading>{ event.venue.name }</Subheading>
                    { event.venue.address && <Text>{ event.venue.address }</Text> }
                  </span> }
                  { event.warehouse && <span className="flex flex-row gap-2 items-center">
                    <BuildingStorefrontIcon className="h-6 w-6" />
                    <Subheading>{ event.warehouse.name }</Subheading>
                    { event.warehouse.address && <Text>{ event.warehouse.address }</Text> }
                  </span> }
                </div>
                { event && <div className="flex flex-row gap-3">
                  <span>
                    <Button color="blue" href={event.publicLink}>
                      Customer link
                    </Button>
                  </span>
                  { event && <span><Button color="amber" onClick={onClone}>Clone event</Button> </span> }
                  {
                    event.state == 'pending' &&
                      <span>
                        <Button color="green" onClick={onConfirm}>
                          Confirm event { mutationLoading && <BounceLoader color="lightgray" size={16} /> }
                        </Button>
                      </span>
                  }
                  {
                    event.state == 'confirmed' &&
                      <span>
                        <Button color="amber" onClick={onPending}>
                          Unconfirm event { mutationLoading && <BounceLoader color="lightgray" size={16} /> }
                        </Button>
                      </span>
                  }
                  {
                    event.state == 'cancelled' &&
                      <span>
                        <Button color="amber" onClick={onPending}>
                          Uncancel event { mutationLoading && <BounceLoader color="lightgray" size={16} /> }
                        </Button>
                      </span>
                  }
                  {
                    event.state != 'cancelled' &&
                      <span>
                        <Button color="red" onClick={onCancel}>
                          Cancel event { mutationLoading && <BounceLoader color="lightgray" size={16} /> }
                        </Button>
                      </span>
                  }
                </div> }
              </div>
          }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { event && <div className="flex flex-col gap-4">
            <div className="grid grid-cols-4 items-center align-center">
              <Text className="flex flex-row gap-2">
                <Strong>Out</Strong> { format(event.dateOut, 'KK:mm dd/MM/yyyy') }
              </Text>
              <Text className="flex flex-row gap-2">
                <Strong>Start</Strong> { format(event.dateStart, 'KK:mm dd/MM/yyyy') }
              </Text>
              <Text className="flex flex-row gap-2">
                <Strong>Finish</Strong> { format(event.dateEnd, 'KK:mm dd/MM/yyyy') }
              </Text>
              <Text className="flex flex-row gap-2">
                <Strong>Return</Strong> { format(event.dateReturn, 'KK:mm dd/MM/yyyy') }
              </Text>
            </div>
            <Divider />
            { event.details && <Accordion defaultOpen={true} title="Details">
              <Text className="whitespace-pre-wrap">{ event.details }</Text>
            </Accordion> }
            <Accordion title="Contacts">
              <ContactsTable eventId={event.id} pageSize={5} fixedHeight />
            </Accordion>
            <Accordion title="Stages">
              <EventStagesTable eventId={event.id} fixedHeight />
            </Accordion>
            <Accordion title="Kit list">
              <EventItemsTable eventId={event.id} fixedHeight />
            </Accordion>
            <Accordion title="Shifts">
              <ShiftsTable filters={{ eventIdEq: event.id }} hiddenColumns={['Event']} pageSize={5} fixedHeight />
            </Accordion>
            <Accordion title="Documents">
              <DocumentsTable filters={{ subjectIdEq: event.id, subjectTypeEq: DocumentSubjectType.Event }} pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Images">
              <AttachmentsCollection
                filters={{ nameEq: 'images', resourceIdEq: event.id, resourceTypeEq: AttachmentResourceType.Event }}
                pageSize={20}
              />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { event && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { event && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { event && <EditDialog id={event.id} open={openEdit} setOpen={setOpenEdit} /> }
      { event && <CloneDialog eventId={event.id} open={openClone} setOpen={setOpenClone} /> }
      {
        event &&
          <DeleteDialog
            id={event.id}
            mutation={DeleteMutation}
            open={openDelete}
            refetchQueries={[TableQuery]}
            setOpen={setOpenDelete}
            onSuccess={onDeleteSuccess}
          />
      }
    </>
  )
}
